import { computed, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { UseRolePermission, useRolePermission } from '@/common/permission/permission.utils';
import {
  convertRequestTime,
  getTzTimeNow,
  utcZeroTimeToStandardTime,
} from '@/common/utils/commonUtils';

import {
  IdInfo,
  NoticeDetailView,
  NoticeInfoResponse,
  NoticeOverView,
  NoticeOverViewEmailRequest,
  PopupPeriod,
  PostPopupInfoResponse,
} from '@/api-client/notice/model';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { makeRows } from '@/common/utils/gridUtils';
import { noticeListColumns } from '@/config/views/management/notice/noticeList.setup';
import { useInternational } from '@/common/locale';
import { isDuplicateNameError } from '@/common/utils/error.utils';
import dayjs from 'dayjs';
import { webStorageController } from '@/common/utils/browser';
import { checkRole } from '@/common/auth/auth.utils';
import { useAbortApi } from '@/common/utils/apiUtils';
import { RoleId } from '@/common/auth/auth.defines';
import { useRepeat } from '@/worker/composables/useRepeat';
import { ONE_MINUTE } from '@/common/utils';
import { useMyInfoStore } from '@/config/stores/my-info';
import {
  getV8NoticeByNoticeId,
  getV8NoticeDetailByNoticeId,
  getV8NoticeList,
  postV8NoticeTestEmailByNoticeId,
  postV8NoticeSave,
  patchV8NoticeUpdateByNoticeId,
  postV8NoticeDelete,
  getV8NoticePopupCheck,
  getV8NoticePopup,
  postV8NoticeClickedByNoticeId,
} from '@/api-client/notice/queries/notice-controller/notice-controller';

export interface NoticeWindowInfo extends PostPopupInfoResponse {
  clicked?: boolean;
}

interface NoticePopupStatus {
  isShow: boolean;
  operationType: 'popup' | 'home';
}

type ResultMessage = 'success' | 'failure';
export const useApi = () => {
  const { t } = useInternational();

  const { isPermissionDenied } = useRolePermission();
  const PERMISSION_DENIED = 'PERMISSION_DENIED';

  const refreshTime = ref<string>('');

  const checkPermissionDeny = (type: UseRolePermission['type'], showError = true) => {
    return isPermissionDenied({
      type,
      rolePermissionKey: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_NOTICE,
      showError,
    });
  };
  const getErrorMessage = (e: any) => {
    if (e?.message === PERMISSION_DENIED) {
      return t('NOTI.API.NOT_PERMISSION');
    }
    if (isDuplicateNameError(e)) {
      return t('ERROR.API.DuplicateNameException');
    }
    return t('NOTI.COMMON_CRUD.SAVE_FAIL');
  };

  // NoticeList
  const getNoticeList = async ({
    frameName,
    isHomeView = false,
  }: {
    frameName: string;
    isHomeView?: boolean;
  }): Promise<{ data: NoticeOverView[]; refreshTime: string; error?: any }> => {
    try {
      const { data } = await getV8NoticeList(
        {
          isHomeView,
        },
        {
          params: {
            frameName,
          },
        },
      );
      refreshTime.value = convertRequestTime();

      return {
        data: data?.data ?? [],
        refreshTime: refreshTime.value,
      };
    } catch (e) {
      console.log(e);
      return {
        data: [],
        refreshTime: refreshTime.value,
        error: getErrorMessage(e),
      };
    }
  };
  // NoticeDetail
  const getNoticeDetailInfo = async ({
    noticeId,
    frameName,
  }: {
    noticeId: number;
    frameName: string;
  }): Promise<{ data: NoticeDetailView[]; refreshTime?: string; error?: any }> => {
    try {
      const { data } = await getV8NoticeDetailByNoticeId(noticeId, {
        params: {
          frameName,
        },
      });

      return {
        data: data?.data ?? [],
      };
    } catch (e) {
      console.log(e);
      return {
        data: [],
        error: getErrorMessage(e),
      };
    }
  };
  // NoticeEditInfo
  const getNoticeEditInfo = async ({
    noticeId,
    frameName,
  }: {
    noticeId: number;
    frameName: string;
  }): Promise<{ data: NoticeInfoResponse[]; refreshTime?: string; error?: any }> => {
    try {
      const { data } = await getV8NoticeByNoticeId(noticeId, {
        params: {
          frameName,
        },
      });

      return {
        data: data?.data ?? [],
      };
    } catch (e) {
      console.log(e);
      return {
        data: [],
        error: getErrorMessage(e),
      };
    }
  };

  const testEmailNotice = async ({
    noticeId,
    frameName,
  }: {
    noticeId: number;
    frameName: string;
  }): Promise<{ resultMsg: ResultMessage; error?: any }> => {
    try {
      const { data } = await postV8NoticeTestEmailByNoticeId(noticeId, {
        params: {
          frameName,
        },
      });
      return {
        resultMsg: data?.status === 'success' ? 'success' : 'failure',
        error: data?.status === 'failure' ? getErrorMessage(null) : null,
      };
    } catch (e: any) {
      console.log(e);
      return {
        resultMsg: 'failure',
        error: getErrorMessage(e),
      };
    }
  };

  const addNotice = async ({
    request,
    frameName,
  }: {
    request: any;
    frameName: string;
  }): Promise<{ resultMsg: ResultMessage; error?: any }> => {
    try {
      if (checkPermissionDeny('add', false)) {
        throw new Error(PERMISSION_DENIED);
      }

      const { data } = await postV8NoticeSave(request, {
        params: {
          frameName,
        },
      });
      return {
        resultMsg: data?.status === 'success' ? 'success' : 'failure',
        error: data?.status === 'failure' ? getErrorMessage(null) : null,
      };
    } catch (e: any) {
      console.log(e);
      return {
        resultMsg: 'failure',
        error: getErrorMessage(e),
      };
    }
  };
  const editNotice = async ({
    noticeId,
    request,
    frameName,
  }: {
    noticeId: number;
    request: any;
    frameName: string;
  }): Promise<{ resultMsg: ResultMessage; error?: any }> => {
    try {
      if (checkPermissionDeny('edit', false)) {
        throw new Error(PERMISSION_DENIED);
      }

      const { data } = await patchV8NoticeUpdateByNoticeId(noticeId, request, {
        params: {
          frameName,
        },
      });
      return {
        resultMsg: data?.status === 'success' ? 'success' : 'failure',
        error: data?.status === 'failure' ? getErrorMessage(null) : null,
      };
    } catch (e: any) {
      console.log(e);
      return {
        resultMsg: 'failure',
        error: getErrorMessage(e),
      };
    }
  };

  const deleteNotice = async ({
    noticeIds,
    frameName,
  }: {
    noticeIds: Array<IdInfo>;
    frameName: string;
  }): Promise<{ resultMsg: ResultMessage; error?: any }> => {
    try {
      if (checkPermissionDeny('delete', false)) {
        throw new Error(PERMISSION_DENIED);
      }

      const { data } = await postV8NoticeDelete(noticeIds, {
        params: {
          frameName,
        },
      });
      return {
        resultMsg: data?.status === 'success' ? 'success' : 'failure',
        error: data?.status === 'failure' ? getErrorMessage(null) : null,
      };
    } catch (e) {
      console.log(e);
      return {
        resultMsg: 'failure',
        error: getErrorMessage(e),
      };
    }
  };

  // 공지사항 Popup List
  // 로그인 전
  const fetchLoginBeforePopupList = async ({
    frameName,
    signal,
  }: {
    frameName: string;
    signal?: AbortSignal;
  }): Promise<{ data: PostPopupInfoResponse[]; refreshTime?: string; error?: any }> => {
    try {
      const { data } = await getV8NoticePopupCheck(
        {
          params: {
            frameName,
          },
        },
        signal,
      );

      return {
        data: (data?.data ?? []).map((item) => ({ ...item, clicked: true })),
        refreshTime: data.requestTime ? utcZeroTimeToStandardTime(data.requestTime) : '',
      };
    } catch (e) {
      console.log(e);
      return {
        data: [],
        refreshTime: '',
        error: getErrorMessage(e),
      };
    }
  };

  // 로그인 후 공지사항 팝업
  const fetchLoginAfterPopupList = async ({
    frameName,
    signal,
  }: {
    frameName: string;
    signal?: AbortSignal;
  }): Promise<{ data: PostPopupInfoResponse[]; refreshTime?: string; error?: any }> => {
    try {
      const { data } = await getV8NoticePopup(
        {
          params: {
            frameName,
          },
        },
        signal,
      );

      return {
        data: (data?.data ?? []).map((item) => {
          return {
            activeId: item.activeId,
            content: item.content,
            id: item.id,
            name: item.name,
            noticeCreatedDate: item.noticeCreatedDate,
            noticeStartDate: item.noticeStartDate,
            popupPeriod: item.popupPeriod,
            title: item.title,
            clicked: item.clicked,
            roleId: item.roleId,
          };
        }),
        refreshTime: data.requestTime ? utcZeroTimeToStandardTime(data.requestTime) : '',
      };
    } catch (e) {
      console.log(e);

      return {
        data: [],
        refreshTime: '',
        error: getErrorMessage(e),
      };
    }
  };

  const postClickedHistory = async (noticeId) => {
    try {
      await postV8NoticeClickedByNoticeId(noticeId);
    } catch (e) {
      console.log(e);
    }
  };
  return {
    checkPermissionDeny,
    getNoticeList,
    getNoticeDetailInfo,
    getNoticeEditInfo,
    addNotice,
    editNotice,
    deleteNotice,
    testEmailNotice,
    fetchLoginBeforePopupList,
    fetchLoginAfterPopupList,
    postClickedHistory,
  };
};
export interface NoticeList {
  noticeChannel?: string;
  emailChannel?: boolean;
  popupChannel?: boolean;
  emailRequest?: NoticeOverViewEmailRequest | '';
  emailRequestTimezone?: string;
  emailSendTime?: string;
  id?: number;
  name?: string;
  noticeStartDate?: string;
  popupNoticeStartDate?: string;
  popupPeriod?: PopupPeriod;
  popupNoticeEndDate?: string;
  noticeStartTimezone?: string;
  registrationTime?: string;
  temporarySaved?: boolean;
  title?: string;
  clicked?: boolean;
}

export const useNoticeStore = defineStore('useNoticeStore', () => {
  const { myInfo } = storeToRefs(useMyInfoStore());
  const { t } = useInternational();
  const {
    getNoticeList,
    getNoticeDetailInfo,
    getNoticeEditInfo,
    checkPermissionDeny,
    fetchLoginBeforePopupList,
    fetchLoginAfterPopupList,
    postClickedHistory,
  } = useApi();
  const noticePopupStatus = ref<NoticePopupStatus>({
    isShow: false,
    operationType: 'popup',
  });

  const isLogin = computed(() => {
    const { userId } = myInfo.value;
    return userId > 0;
  });

  const canAdd = !checkPermissionDeny('add', false);
  const canEdit = !checkPermissionDeny('edit', false);
  const canDelete = !checkPermissionDeny('delete', false);

  const hasPermission = computed(() => ({
    add: canAdd && !checkRole.isUser(myInfo.value.role),
    edit: canEdit && !checkRole.isUser(myInfo.value.role),
    delete: canDelete && !checkRole.isUser(myInfo.value.role),
  }));

  const expandNoticeId = ref<number>(0);
  const noticeListRows = ref<NoticeList[]>([]);
  const filterData = ref<NoticeList[]>([]);
  const refreshTime = ref<string>('');
  const isFailRequestTime = ref<boolean>(false);
  const selectedNameList = ref<any>([]);

  const refresh = async () => {
    const data = await getNoticeList({
      frameName: FRAME_NAMES.SETTING_NOTICE.GET_NOTICE_LIST,
      isHomeView: false,
    });

    const formatDate = (date) =>
      date ? dayjs(utcZeroTimeToStandardTime(date)).format('YYYY-MM-DD HH:mm') : '';

    filterData.value = data.data.map((item) => {
      const email = item.emailChannel ? t('WORD.EMAIL') : '';
      const popup = item.popupChannel ? t('WORD.POPUP') : '';

      const name = checkRole.isMaster(item.roleId as unknown as RoleId)
        ? t('WORD.SYSTEM')
        : item.name || t('WORD.UNIDENTIFIED_USER');

      return {
        ...item,
        name,
        noticeChannel: [email, popup].filter((channel) => channel !== '').join(','),
        noticeStartDate: formatDate(item.noticeStartDate),
        emailSendTime: formatDate(item.emailSendTime),
        popupNoticeStartDate: formatDate(item.popupPeriod?.fromTime),
        popupNoticeEndDate: formatDate(item.popupPeriod?.toTime),
        emailRequest:
          item.emailRequest === NoticeOverViewEmailRequest.NONE ? '' : item.emailRequest,
      };
    });

    isFailRequestTime.value = !!data.error;

    noticeListRows.value = makeRows(filterData.value, noticeListColumns);
    refreshTime.value = data.refreshTime;
  };
  const isOverDate = ref(false);
  const today = getTzTimeNow();

  const noticeDetailInfo = ref<NoticeDetailView>({ content: '' });
  const noticeEditInfo = ref<NoticeInfoResponse>({});
  const getDetailInfo = async (noticeId: number) => {
    const data = await getNoticeDetailInfo({
      noticeId,
      frameName: FRAME_NAMES.SETTING_NOTICE.GET_NOTICE_DETAIL,
    });
    const resData = data.data[0];
    selectedNameList.value = resData.recipients?.map((item) => item.name);
    resData.content = resData.content ? resData.content : '';

    isOverDate.value = dayjs(utcZeroTimeToStandardTime(resData.noticeStartDate)) < today;
    noticeDetailInfo.value = resData;
  };
  const getEditInfo = async (noticeId: number) => {
    const data = await getNoticeEditInfo({
      noticeId,
      frameName: FRAME_NAMES.SETTING_NOTICE.GET_NOTICE_EDIT_DETAIL,
    });
    const resData = data.data[0];
    noticeEditInfo.value = resData;
  };

  const homeNoticeList = ref<NoticeOverView[]>([]);
  const getHomeNoticeList = async () => {
    const { data } = await getNoticeList({
      frameName: FRAME_NAMES.HOME.NOTICE_LIST,
      isHomeView: true,
    });

    homeNoticeList.value = data ?? [];
  };

  const { abortApi, getSignal } = useAbortApi();
  const popupInfo = ref<NoticeWindowInfo[]>([]);
  const popupInterval = ref<number>(ONE_MINUTE);

  const getNoticePopupInfo = async () => {
    abortApi();

    if (!isLogin.value) {
      const data = await fetchLoginBeforePopupList({
        frameName: FRAME_NAMES.SETTING_NOTICE.GET_POPUP_CHECK,
        signal: getSignal(),
      });
      popupInfo.value = data.data;
    } else {
      const data = await fetchLoginAfterPopupList({
        frameName: FRAME_NAMES.HOME.NOTICE_LIST,
        signal: getSignal(),
      });
      popupInfo.value = data.data;
    }
  };

  const checkPopupInfo = async () => {
    const tempHiddenRaw = sessionStorage.getItem('noticePopupTempHiddenInfo');
    if (tempHiddenRaw) {
      let isChangeData = false;
      let hasIdsNotInPopup = true;

      try {
        const tempData = JSON.parse(tempHiddenRaw);
        hasIdsNotInPopup = popupInfo.value.some(
          (item) => !tempData?.popupInfo?.some((popup: any) => popup.id === item.id),
        );

        if (!hasIdsNotInPopup) {
          const isAllMatchedPopupInfo = popupInfo.value.every((item) =>
            tempData.popupInfo?.some(
              (popup: any) => popup.id === item.id && popup.title === item.title,
            ),
          );

          if (!isAllMatchedPopupInfo) {
            isChangeData = true;
          }
        }
      } catch {
        isChangeData = true;
      }

      if (!hasIdsNotInPopup && !isChangeData && popupInfo.value.length > 0) {
        noticePopupStatus.value = { isShow: false, operationType: 'popup' };
        return;
      }

      sessionStorage.removeItem('noticePopupTempHiddenInfo');
    }

    const popupData = JSON.parse(
      webStorageController.getItem({
        type: 'local',
        key: 'noticePopupInfo',
      }) || '{}',
    );

    const isNotShowPopupCheck = popupData.isShowPopupCheck;

    const hasIdsNotInPopup =
      Object.keys(popupData).length > 0
        ? popupInfo.value.some(
            (item) => !popupData.popupInfo?.some((popup) => popup.id === item.id),
          )
        : true;
    let isChangeData = false;

    if (!hasIdsNotInPopup) {
      const isAllMatchedPopupInfo = popupInfo.value.every((item) =>
        popupData.popupInfo.some((popup) => popup.id === item.id && popup.title === item.title),
      );

      if (!isAllMatchedPopupInfo) {
        isChangeData = true;
      }
    }

    let shouldShow = popupInfo.value.length > 0;

    if (shouldShow) {
      const hiddenByLocal = isNotShowPopupCheck && !hasIdsNotInPopup && !isChangeData;
      if (hiddenByLocal) {
        shouldShow = false;
      }
    }

    noticePopupStatus.value = { isShow: shouldShow, operationType: 'popup' };
  };

  // interval 관리를 위한 useRepeat 설정
  const { resetFetch, clearFetch } = useRepeat(
    async () => {
      await getNoticePopupInfo();
      await checkPopupInfo();
    },
    popupInterval.value,
    { isSetup: false, isImmediate: false },
    abortApi,
  );

  let isPopupIntervalRunning = false;

  const startPopupInfoInterval = async () => {
    if (isPopupIntervalRunning) return;
    isPopupIntervalRunning = true;
    resetFetch({}, popupInterval.value);
  };

  const stopPopupInfoInterval = async () => {
    isPopupIntervalRunning = false;
    await clearFetch({ immediate: true });
  };

  const setClicked = async (noticeId) => {
    if (!isLogin.value || !noticeId) return;
    await postClickedHistory(noticeId);

    popupInfo.value.forEach((item) => {
      if (item.id === noticeId) {
        item.clicked = true;
      }
    });

    homeNoticeList.value.forEach((item) => {
      if (item.id === noticeId) {
        item.clicked = true;
      }
    });
  };

  return {
    hasPermission,
    refreshTime,
    isFailRequestTime,
    isOverDate,
    refresh,

    noticeListRows,
    noticeDetailInfo,
    noticeEditInfo,
    selectedNameList,
    noticePopupStatus,
    expandNoticeId,
    checkPermissionDeny,
    getDetailInfo,
    getEditInfo,

    homeNoticeList,
    getHomeNoticeList,

    popupInfo,
    popupInterval,
    getNoticePopupInfo,
    startPopupInfoInterval,
    stopPopupInfoInterval,
    checkPopupInfo,
    setClicked,
    isLogin,
  };
});
